/* eslint-disable react-hooks/exhaustive-deps */
import { useAdminUser, useAlert } from '@orientaction/hooks';
import { adminService } from '@orientaction/services';
import { getFromLS, getHeaders, makeRequest } from '@orientaction/utils';
import { generate } from 'generate-password-browser';
import { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import config from '../../../services/config';
import { getUserById } from '../../../services/user.service';
import { apiServiceNative } from '../../../utils/fetchNative';
import {
  BENEFICIARY_PROFILE_TABS,
  BeneficiaryProfileTabId,
  DELETE_PRESTATION_SUCCESS,
  DELETE_USER_SUCCESS,
  DISABLE_USER_SUCCESS,
  ENABLE_USER_SUCCESS,
} from './constants';
import { CONSULTANT_ROLE_ID } from '../shared/constants';
import { useAssignmentResourceFilter } from './useAssignmentResourceFilter';
import { getConsultantDisplayForService, getConsultantIdForService, getServiceName, toLanguageId } from './utils';
import { useNavigation } from '../../../Context/NavigationContext';

const qs = require('qs');

type ConfirmType = 'disable' | 'enable' | 'delete' | '';

interface PrestationConfirmState {
  open: boolean;
  service: { id: number; name: string } | null;
  consultantName: string;
}

const emptyPrestationConfirm: PrestationConfirmState = {
  open: false,
  service: null,
  consultantName: '',
};

const VALID_TAB_IDS = new Set(BENEFICIARY_PROFILE_TABS.map(item => item.id as string));

const readInitialTab = (search: string): BeneficiaryProfileTabId => {
  const params = new URLSearchParams(search);
  const requested = params.get('tab');
  if (requested && VALID_TAB_IDS.has(requested)) {
    return requested as BeneficiaryProfileTabId;
  }
  return 'profil';
};

export const useBeneficiaryProfile = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const location = useLocation();
  const alert = useAlert();
  const { setTabs } = useAdminUser();
  const { setTab: setListTab } = useNavigation();
  const token = getFromLS('userToken') || '';

  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<BeneficiaryProfileTabId>(() => readInitialTab(location.search));
  const [confirmModal, setConfirmModal] = useState<{ open: boolean; type: ConfirmType }>({
    open: false,
    type: '',
  });
  const [confirming, setConfirming] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [generatingPassword, setGeneratingPassword] = useState(false);
  const [passwordModal, setPasswordModal] = useState({ open: false, password: '' });
  const [prestationConfirm, setPrestationConfirm] =
    useState<PrestationConfirmState>(emptyPrestationConfirm);
  const [deletingPrestation, setDeletingPrestation] = useState(false);
  const assignmentFilter = useAssignmentResourceFilter(user);

  const fetchLanguage = async () => {
    try {
      const response = await makeRequest(
        `${config.api_url}/api/users-permissions/get-language/${id}`,
        'GET',
        {},
        getHeaders(token)
      );
      return response?.data?.data?.language ?? response?.data?.language ?? null;
    } catch {
      return null;
    }
  };

  const fetchPrestationsContext = async () => {
    const params = qs.stringify(
      {
        populate: {
          service: { fields: ['id', 'name'] },
          services: { fields: ['id', 'name'] },
          upper_hierarchy: { fields: ['id', 'firstname', 'lastname'] },
          upper_hierarchies: { fields: ['id', 'firstname', 'lastname'] },
          service_assignments: {
            populate: {
              service: { fields: ['id', 'name'] },
              upper_hierarchy: { fields: ['id', 'firstname', 'lastname'] },
              tests_to_do: { fields: ['id', 'title', 'name'] },
              major_tests: { fields: ['id', 'name'] },
              book_templates: { fields: ['id', 'name'] },
              games: { fields: ['id', 'title'] },
              ebooks: { fields: ['id', 'title'] },
            },
          },
          tests: { fields: ['id', 'name'] },
          tests_to_do: { fields: ['id', 'title', 'name'] },
        },
      },
      { encodeValuesOnly: true }
    );
    try {
      return await apiServiceNative.get(`/api/users/${id}?${params}`);
    } catch {
      return null;
    }
  };

  const fetchUser = async (saved?: any) => {
    const [response, languageFromApi, prestationsContext] = await Promise.all([
      getUserById({ token, userId: id }),
      fetchLanguage(),
      fetchPrestationsContext(),
    ]);
    const next = response?.data || response;
    const context = prestationsContext?.data || prestationsContext || {};
    const savedId = toLanguageId(saved?.language);
    const apiId = toLanguageId(languageFromApi) || toLanguageId(next?.language);
    const languageId = savedId || apiId;
    let language = null;
    if (languageFromApi && toLanguageId(languageFromApi)) {
      language = languageFromApi;
    } else if (languageId) {
      language = { id: languageId };
    }
    setUser({
      ...next,
      language,
      service: context.service ?? next?.service,
      services: context.services ?? next?.services,
      upper_hierarchy: context.upper_hierarchy ?? next?.upper_hierarchy,
      upper_hierarchies: context.upper_hierarchies ?? next?.upper_hierarchies,
      service_assignments: context.service_assignments ?? next?.service_assignments,
      service_resources_map: context.service_resources_map ?? next?.service_resources_map,
      tests: context.tests ?? next?.tests,
      tests_to_do: context.tests_to_do ?? next?.tests_to_do,
    });
  };

  const loadUser = async () => {
    setLoading(true);
    try {
      await fetchUser();
    } catch (error) {
      alert.openSimpleAlert();
      history.push('/admin/users-resultats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUser();
  }, [id]);

  const goBack = () => {
    setTabs(5);
    setListTab(5);
    history.push('/admin/users-resultats');
  };

  const canManagePrestation = (serviceId: number) => {
    const connectedUser = JSON.parse(getFromLS('userInfos') || '{}');
    if (Number(connectedUser?.company_user_role?.id) !== CONSULTANT_ROLE_ID) {
      return true;
    }
    const ownerId = getConsultantIdForService(user, serviceId);
    return ownerId != null && ownerId === Number(connectedUser?.id);
  };

  const goToAddPrestation = () => {
    if (!id) {
      return;
    }
    history.push(`/admin/users-resultats/beneficiaire/${id}/ajouter-prestation`);
  };

  const goToEditPrestation = (service: { id: number }) => {
    if (!id || !service?.id || !canManagePrestation(service.id)) {
      return;
    }
    history.push(`/admin/users-resultats/beneficiaire/${id}/prestation/${service.id}`);
  };

  const closeConfirmModal = () => {
    if (confirming) {
      return;
    }
    setConfirmModal(prev => ({ ...prev, open: false }));
  };

  const closePasswordModal = () => {
    setPasswordModal({ open: false, password: '' });
  };

  const generatePassword = async () => {
    if (!user || generatingPassword) {
      return;
    }
    setGeneratingPassword(true);
    try {
      const generatedPassword = generate({
        length: 10,
        lowercase: true,
        uppercase: true,
        numbers: true,
        symbols: true,
        exclude: '$',
        strict: true,
      });
      await adminService.generateUserPassword(user.id, generatedPassword, token);
      setPasswordModal({ open: true, password: generatedPassword });
    } catch (error) {
      alert.openSimpleAlert();
    } finally {
      setGeneratingPassword(false);
    }
  };

  const askDisable = () => {
    if (!user) {
      return;
    }
    setConfirmModal({
      open: true,
      type: user.isActivated ? 'disable' : 'enable',
    });
  };

  const askDelete = () => {
    setConfirmModal({
      open: true,
      type: 'delete',
    });
  };

  const confirmAction = async () => {
    if (!user || !confirmModal.type) {
      return;
    }
    setConfirming(true);
    try {
      if (confirmModal.type === 'disable' || confirmModal.type === 'enable') {
        await adminService.disableAdmin(user.id, !user.isActivated, token);
        await fetchUser();
        setConfirmModal(prev => ({ ...prev, open: false }));
        alert.setOpenAlert(
          true,
          confirmModal.type === 'disable' ? DISABLE_USER_SUCCESS : ENABLE_USER_SUCCESS,
          'success'
        );
        return;
      }
      if (confirmModal.type === 'delete') {
        await adminService.deleteAdmin({ id: user.id, companyUserRole: 5 }, token);
        setConfirmModal(prev => ({ ...prev, open: false }));
        setTabs(5);
        setListTab(5);
        alert.setOpenAlert(true, DELETE_USER_SUCCESS, 'success');
        history.push('/admin/users-resultats');
      }
    } catch (error) {
      alert.openSimpleAlert();
    } finally {
      setConfirming(false);
    }
  };

  const askDeletePrestation = (service: { id: number; name?: string }) => {
    if (!user || !service?.id || !canManagePrestation(service.id)) {
      return;
    }
    setPrestationConfirm({
      open: true,
      service: { id: Number(service.id), name: getServiceName(service) },
      consultantName: getConsultantDisplayForService(user, service.id),
    });
  };

  const closePrestationConfirm = () => {
    if (deletingPrestation) {
      return;
    }
    setPrestationConfirm(emptyPrestationConfirm);
  };

  const confirmDeletePrestation = async () => {
    const target = prestationConfirm.service;
    if (!user || !target) {
      return;
    }
    setDeletingPrestation(true);
    try {
      await apiServiceNative.put(`/api/company-user-admin-remove-service/${user.id}`, {
        service: { id: Number(target.id) },
      });

      await fetchUser();
      setPrestationConfirm(emptyPrestationConfirm);
      alert.setOpenAlert(true, DELETE_PRESTATION_SUCCESS, 'success');
    } catch (error) {
      alert.openSimpleAlert();
    } finally {
      setDeletingPrestation(false);
    }
  };

  return {
    user,
    loading,
    tab,
    setTab,
    editingProfile,
    setEditingProfile,
    fetchUser,
    confirmModal,
    confirming,
    generatingPassword,
    passwordModal,
    goBack,
    goToAddPrestation,
    goToEditPrestation,
    generatePassword,
    closePasswordModal,
    askDisable,
    askDelete,
    closeConfirmModal,
    confirmAction,
    prestationConfirm,
    deletingPrestation,
    askDeletePrestation,
    closePrestationConfirm,
    confirmDeletePrestation,
    assignmentFilter,
  };
};
